import type { RouteObject } from 'react-router';

import React, { lazy } from 'react';
import { Outlet } from 'react-router';

import { AppDashboardLayout } from '@layouts/dashboard';

import { RouteError } from '@shared/components/route-error';

import { ShopProvider } from '@features/shop';
import { PrinterProvider } from '@features/printer';
import { WarehouseProvider } from '@features/warehouse';
import { WorkshopRealtime } from '@features/workshop-plan';
import { Role, RoleGuard, UserProvider } from '@features/user-access';

// Login
const SignIn = lazy(() => import('@pages/auth/sign-in'));
// Dashboard
const Dashboard = lazy(() => import('@pages/dashboard'));
// Shop
const ShopProductInformation = lazy(() => import('@pages/shop/product-information'));
// Warehouse
const WarehousePrintBox = lazy(() => import('@pages/warehouse/print-box'));
const WarehouseCarrierSlip = lazy(() => import('@pages/warehouse/carrier-slip'));
const WarehousePrintLocation = lazy(() => import('@pages/warehouse/print-location'));
const WarehousePrintProduct = lazy(() => import('@pages/warehouse/print-product'));
const WarehouseShipment = lazy(() => import('@pages/warehouse/shipment'));
const WarehouseTransfer = lazy(() => import('@pages/warehouse/transfer'));
const WarehousePackaging = lazy(() => import('@pages/warehouse/packaging'));
const WarehouseOrderInformation = lazy(() => import('@pages/warehouse/order-information'));
const WarehouseOrderReturn = lazy(() => import('@pages/warehouse/order-return'));
const WarehousePicking = lazy(() => import('@pages/warehouse/picking'));
const WarehousePickingShop = lazy(() => import('@pages/warehouse/picking-shop'));
const WarehousePickingSession = lazy(() => import('@pages/warehouse/picking-session'));
const WarehouseIncomingParcelScan = lazy(() => import('@pages/warehouse/incoming-parcel-scan'));
const WarehouseReception = lazy(() => import('@pages/warehouse/reception'));
const WarehouseRebut = lazy(() => import('@pages/warehouse/rebut'));
const WarehouseInventory = lazy(() => import('@pages/warehouse/inventory'));
const WarehouseProductInformation = lazy(() => import('@pages/warehouse/product-information'));
// Dev (kit UI, hors production)
const DevUiKit = lazy(() => import('@pages/dev/ui-kit'));
// Workshop
const WorkshopHome = lazy(() => import('@pages/workshop'));
const WorkshopStorage = lazy(() => import('@pages/workshop/storage'));
const WorkshopRelease = lazy(() => import('@pages/workshop/release'));
const WorkshopPlan = lazy(() => import('@pages/workshop/plan'));
const WorkshopSessions = lazy(() => import('@pages/workshop/sessions'));
const WorkshopSession = lazy(() => import('@pages/workshop/session'));

export const routes: RouteObject[] = [
  {
    path: '/login',
    element: <SignIn />,
    errorElement: <RouteError fullPage />,
  },
  {
    path: '/',
    element: (
      <UserProvider>
        <PrinterProvider>
          <AppDashboardLayout />
        </PrinterProvider>
      </UserProvider>
    ),
    errorElement: <RouteError fullPage />,
    children: [
      {
        // A page that crashes keeps the menu around it, so the operator can leave it.
        errorElement: <RouteError />,
        children: [
          {
            index: true,
            element: <Dashboard />,
          },
          ...(import.meta.env.DEV ? [{ path: 'dev/ui-kit', element: <DevUiKit /> }] : []),
          {
            path: 'shop/:shopSlug',
            element: (
              <ShopProvider>
                <Outlet />
              </ShopProvider>
            ),
            children: [
              {
                path: 'product-information',
                element: <ShopProductInformation />,
              },
            ],
          },
          {
            path: 'warehouse',
            element: (
              <WarehouseProvider>
                <Outlet />
              </WarehouseProvider>
            ),
            children: [
              {
                path: 'carrier-slip',
                element: <WarehouseCarrierSlip />,
              },
              {
                path: 'print-box',
                element: <WarehousePrintBox />,
              },
              {
                path: 'print-product',
                element: (
                  <RoleGuard role={Role.Barcode}>
                    <WarehousePrintProduct />
                  </RoleGuard>
                ),
              },
              {
                path: 'print-location',
                element: <WarehousePrintLocation />,
              },
              {
                path: 'shipment',
                element: <WarehouseShipment />,
              },
              {
                path: 'transfer',
                element: <WarehouseTransfer />,
              },
              {
                path: 'packaging',
                element: <WarehousePackaging />,
              },
              {
                path: 'order-information',
                element: <WarehouseOrderInformation />,
              },
              {
                path: 'order-return',
                element: <WarehouseOrderReturn />,
              },
              {
                path: 'picking',
                element: <WarehousePicking />,
              },
              {
                path: 'picking-session',
                element: <WarehousePickingSession />,
              },
              {
                path: 'incoming-parcel-scan',
                element: (
                  <RoleGuard role={Role.IncomingParcelScan}>
                    <WarehouseIncomingParcelScan />
                  </RoleGuard>
                ),
              },
              {
                path: 'reception',
                element: (
                  <RoleGuard role={Role.Reception}>
                    <WarehouseReception />
                  </RoleGuard>
                ),
              },
              {
                path: 'inventory',
                element: (
                  <RoleGuard role={Role.Inventory}>
                    <WarehouseInventory />
                  </RoleGuard>
                ),
              },
              {
                path: 'rebut',
                element: (
                  <RoleGuard role={Role.Rebut}>
                    <WarehouseRebut />
                  </RoleGuard>
                ),
              },
              {
                path: 'product-information',
                element: (
                  <RoleGuard role={Role.Info}>
                    <WarehouseProductInformation />
                  </RoleGuard>
                ),
              },
              {
                path: 'picking/:shopSlug',
                element: (
                  <ShopProvider>
                    <WarehousePickingShop />
                  </ShopProvider>
                ),
              },
            ],
          },
          {
            path: 'workshop',
            element: (
              <WarehouseProvider>
                <RoleGuard role={Role.Workshop}>
                  <WorkshopRealtime />
                  <Outlet />
                </RoleGuard>
              </WarehouseProvider>
            ),
            children: [
              {
                index: true,
                element: <WorkshopHome />,
              },
              {
                path: 'storage',
                element: <WorkshopStorage />,
              },
              {
                path: 'release',
                element: <WorkshopRelease />,
              },
              {
                path: 'plan',
                element: <WorkshopPlan />,
              },
              {
                path: 'sessions',
                element: <WorkshopSessions />,
              },
              {
                path: 'sessions/:id',
                element: <WorkshopSession />,
              },
            ],
          },
        ],
      },
    ],
  },
];
